import { totalDesJours } from '../../lib/pointage';
import { fmtH } from '../../lib/currency';
import { tr } from '../../lib/i18n';
import { useReglages } from '../../api/reglages.jsx';
import { useSession } from '../../api/session.jsx';
import { CLE_TELETRAVAIL_ACCEPTE, constatDuBadge, marqueDuConstat } from '../../lib/lieux-travail';
import { hours } from '../../lib/utils';
import { C } from '../../theme/palette';

/**
 * Le relevé d'heures d'un employé, sur un mois : chantier par chantier, chaque
 * jour avec l'horaire pointé, ce qui a été fait (travaux du pointage et
 * rapport du jour) et les heures.
 *
 * <p>Un mois tient sur une feuille A4 ; le choix du mois ne s'imprime pas. Le
 * patron (fiche employé) et l'employé (son espace) impriment le même relevé.
 *
 * @param {object} props propriétés
 * @param {object} props.emp fiche de l'employé
 * @param {object[]} props.chantiers chantiers (ou lieux) où il a travaillé
 * @param {string} props.accInk couleur du document
 * @param {boolean} [props.magasin] lieu de travail : le magasin
 * @param {string} [props.mois] mois choisi (AAAAMM), « tout », ou rien pour le dernier
 * @param {Function} [props.onMois] reçoit le mois choisi
 * @returns {JSX.Element} le relevé
 */
export function ReleveHeuresDetail({ emp, chantiers, accInk, magasin, mois: moisChoisi, onMois }) {
  // Le télétravail pointé un jour non prévu : le patron (ou le secrétariat)
  // l'accepte d'un geste, et la marque devient « ✓ accepté ».
  const { reglageJson, setReglageJson } = useReglages();
  const { session } = useSession();
  const acceptes = reglageJson(CLE_TELETRAVAIL_ACCEPTE, []) || [];
  const peutAccepter = ['patron', 'secretaire'].includes(session?.role);
  const accepte = (ids) =>
    setReglageJson(CLE_TELETRAVAIL_ACCEPTE, [
      ...new Set([...acceptes.map(String), ...ids.map(String)]),
    ]);
  const norm = (d) => {
    d = (d || '') + '';
    if (d.includes('-')) {
      const z = d.split('-');
      return {
        k: z[0] + (z[1] || '').padStart(2, '0') + (z[2] || '').padStart(2, '0'),
        l: (z[2] || '') + '.' + (z[1] || '') + '.' + z[0],
      };
    }
    const p = d.split('.');
    if (p.length >= 3)
      return {
        k: p[2] + p[1].padStart(2, '0') + p[0].padStart(2, '0'),
        l: p[0].padStart(2, '0') + '.' + p[1].padStart(2, '0') + '.' + p[2],
      };
    return { k: d, l: d };
  };
  // Chaque jour : l'horaire pointé, les heures, et ce qui a été fait
  // (travaux du pointage et rapport du jour), chantier par chantier.
  const tous = chantiers
    .map((c) => {
      const pts = (c.pointages || []).filter((p) => p.empId === emp.id);
      const rs = (c.rapports || []).filter((r) =>
        r.empId != null ? r.empId === emp.id : r.empNom === emp.nom,
      );
      const bd = {};
      const jour = (z) =>
        (bd[z.k] = bd[z.k] || {
          date: z.l,
          k: z.k,
          h: 0,
          e: '',
          s: '',
          trav: [],
          constat: null,
          aAccepter: [],
        });
      // Le plus grave l'emporte : « hors du lieu », puis « à accepter », puis
      // « sur place ».
      const gravite = { hors: 3, accepter: 2, ok: 1, accepte: 1 };
      pts.forEach((p) => {
        const d = jour(norm(p.date));
        const constat = constatDuBadge(p, emp, acceptes);
        if (constat === 'accepter' && p.id != null) d.aAccepter.push(p.id);
        if (constat && (gravite[constat] || 0) > (gravite[d.constat] || 0)) d.constat = constat;
        d.h += hours(p.entree, p.sortie, p.pause);
        if (p.entree) d.e = p.entree;
        if (p.sortie) d.s = p.sortie;
        if (p.tache && !d.trav.includes(p.tache)) d.trav.push(p.tache);
      });
      rs.forEach((r) => {
        const d = jour(norm(r.date));
        if (!d.h && r.heures) d.h = r.heures;
        if (!d.e && r.hd) d.e = r.hd;
        if (!d.s && r.hf) d.s = r.hf;
        if (r.contenu && !d.trav.includes(r.contenu)) d.trav.push(r.contenu);
      });
      return {
        nom: c.nom,
        adr: [c.adr, c.npa].filter(Boolean).join(', '),
        days: Object.values(bd),
        horsChantier: !!c.horsChantier,
      };
    })
    .filter((x) => x.days.length > 0);
  // Un relevé par mois : il tient sur une feuille. Par défaut, le
  // dernier mois travaillé.
  const moisDispo = Array.from(
    new Set(tous.flatMap((x) => x.days.map((d) => (d.k || '').slice(0, 6)))),
  )
    .filter((m) => /^\d{6}$/.test(m))
    .sort()
    .reverse();
  const mois = moisChoisi || moisDispo[0] || 'tout';
  const libMois = (m) =>
    m === 'tout'
      ? tr('Toute la période')
      : new Date(+m.slice(0, 4), +m.slice(4, 6) - 1, 1).toLocaleDateString('fr-CH', {
          month: 'long',
          year: 'numeric',
        });
  const pc = tous
    .map((x) => {
      const days = x.days
        .filter((d) => mois === 'tout' || (d.k || '').startsWith(mois))
        .sort((a, b) => (a.k || '').localeCompare(b.k || ''));
      return { ...x, days, hh: totalDesJours(days) };
    })
    .filter((x) => x.days.length > 0);
  const choixMois = (
    <div className="no-print" style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 8.5, fontWeight: 700, color: C.muted }}>Mois du relevé</label>
      <select
        value={mois}
        onChange={(e) => onMois?.(e.target.value)}
        style={{
          display: 'block',
          marginTop: 3,
          border: '1px solid ' + C.border,
          borderRadius: 8,
          padding: '7px 9px',
          fontSize: 11,
          background: '#fff',
        }}
      >
        {moisDispo.map((m) => (
          <option key={m} value={m}>
            {libMois(m)}
          </option>
        ))}
        <option value="tout">{tr('Toute la période')}</option>
      </select>
    </div>
  );
  const entete = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        border: '1px solid #e2e5ea',
        borderRadius: 8,
        padding: '8px 12px',
        margin: '10px 0 12px',
        fontSize: 10,
      }}
    >
      <div>
        <span style={{ color: '#8a909c' }}>{tr('Employé')} : </span>
        <b style={{ fontSize: 12 }}>{[emp.prenom, emp.nom].filter(Boolean).join(' ') || emp.nom}</b>
        {emp.role ? <span style={{ color: '#5a6270' }}> · {emp.role}</span> : null}
      </div>
      <div>
        <span style={{ color: '#8a909c' }}>{tr('Période')} : </span>
        <b>{libMois(mois)}</b>
      </div>
    </div>
  );
  if (!pc.length)
    return (
      <div>
        {choixMois}
        {entete}
        <div style={{ fontSize: 11, color: '#8a909c', padding: '20px 0' }}>
          Aucune heure pointée sur la période.
        </div>
      </div>
    );
  const tot = pc.reduce((a, x) => a + x.hh, 0);
  // Une colonne de plus quand un jour au bureau, au dépôt ou en télétravail
  // porte un constat.
  const avecConstats = pc.some((x) => x.days.some((d) => d.constat));
  const cel = { padding: '3px 6px 3px 0', verticalAlign: 'top' };
  return (
    <div>
      {choixMois}
      {entete}
      <div
        style={{
          fontSize: 9,
          fontWeight: 800,
          color: accInk,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          marginBottom: 6,
        }}
      >
        {magasin
          ? 'Heures au magasin — détail journalier'
          : pc.some((x) => x.horsChantier)
            ? tr('Lieux de travail — détail journalier')
            : 'Chantiers travaillés — détail journalier'}
      </div>
      {pc.map((x, i) => (
        <div
          key={i}
          style={{
            marginBottom: 9,
            paddingBottom: 6,
            borderBottom: '1px solid #eef0f3',
            pageBreakInside: 'avoid',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 10.5,
              marginBottom: 2,
            }}
          >
            <div style={{ flex: 1, marginRight: 10 }}>
              <b>{x.nom}</b>
              {x.adr ? <span style={{ fontSize: 8.5, color: '#8a909c' }}> · {x.adr}</span> : null}
            </div>
            <span style={{ fontWeight: 800, color: C.teal }}>{fmtH(x.hh)}</span>
          </div>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 8.6,
              color: '#3a4250',
            }}
          >
            <tbody>
              {x.days.map((d, j) => (
                <tr key={j} style={{ borderTop: '1px solid #f2f4f6' }}>
                  <td style={{ ...cel, width: 58, whiteSpace: 'nowrap' }}>{d.date}</td>
                  <td style={{ ...cel, width: 78, whiteSpace: 'nowrap' }}>
                    {d.e ? d.e + (d.s ? ' → ' + d.s : '') : '—'}
                  </td>
                  <td style={{ ...cel, color: '#5a6270' }}>{d.trav.join(' · ')}</td>
                  <td
                    style={{
                      ...cel,
                      paddingRight: 0,
                      width: 40,
                      textAlign: 'right',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fmtH(d.h)}
                  </td>
                  {avecConstats && (
                    <td
                      style={{
                        ...cel,
                        paddingRight: 0,
                        paddingLeft: 6,
                        width: 70,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        fontWeight: 700,
                        color:
                          d.constat === 'hors'
                            ? C.amber
                            : d.constat === 'accepter'
                              ? C.purple
                              : C.green,
                      }}
                    >
                      {marqueDuConstat(d.constat)}
                      {d.constat === 'accepter' && peutAccepter && d.aAccepter.length > 0 && (
                        <button
                          className="no-print"
                          onClick={() => accepte(d.aAccepter)}
                          style={{
                            marginLeft: 6,
                            border: '1px solid ' + C.purple,
                            background: '#fff',
                            color: C.purple,
                            borderRadius: 6,
                            fontSize: 9,
                            fontWeight: 800,
                            padding: '2px 6px',
                            cursor: 'pointer',
                          }}
                        >
                          {tr('✓ Accepter')}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '8px 0',
          fontSize: 12,
          fontWeight: 800,
          borderTop: '2px solid ' + accInk,
          marginTop: 2,
        }}
      >
        <span>Total des heures</span>
        <span style={{ color: C.teal }}>{fmtH(tot)}</span>
      </div>
      {avecConstats && (
        <div style={{ fontSize: 8.5, color: '#5a6270', lineHeight: 1.5, marginTop: 4 }}>
          {tr(
            'Les heures au bureau, au dépôt et en télétravail comptent dans la fiche de paie, pas dans le coût d’un chantier.',
          )}
        </div>
      )}
    </div>
  );
}
